import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import styles from './Chart.module.css';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AuscultationIcon } from '../../../components/icons/Icons';
import { useSelector } from 'react-redux';
import VitalCardHeader from './components/VitalCardHeader';
import { getLatestMeasurement } from '../../../utils/unitUtils';
import { buildDeviceColorMap } from '../../../utils/graphUtils';

const AuscultationCardView = React.forwardRef(({ className, auscData, healthEvaluationData, vitalName, handlePointClick }, ref) => {
  const chartContainerRef = useRef(null);

  const { startDate, endDate } = useSelector((state) => state.patientDetailFilter);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({});
  const vitals = useSelector((state) => state.vital.modalities[vitalName]);
  const [opacity, setOpacity] = useState({});

  const updateData = useCallback(() => {
    // Group data by date and deviceName
    const groupedData = auscData.reduce((acc, entry) => {
      const date = entry.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][entry.deviceName] = entry;
      return acc;
    }, {});

    let transformedData = Object.values(groupedData);
    transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    setData(transformedData);
    const deviceOpacity = auscData.reduce((acc, entry) => {
      const { deviceName } = entry;

      if (!acc[deviceName]) {
        acc[deviceName] = [];
      }
      acc[deviceName].push(entry);

      return acc;
    }, {});

    setOpacity(Object.keys(deviceOpacity).reduce((acc, key) => {
      acc[key] = 1;
      return acc;
    }, {}));
    const latestMeasurementData = getLatestMeasurement(
      auscData,
      vitalName,
      false,
      ' br/min'
    );
    setDisplayData(latestMeasurementData);
  }, [auscData]);

  useEffect(() => {
    updateData();
  }, [updateData, startDate, endDate]);

  const handleLegendClick = (e) => {
    const deviceName = e.value;
    setOpacity((op) => ({ ...op, [deviceName]: op[deviceName] === 1 ? 0 : 1 }));
  };

  const deviceNames = Array.from(new Set(auscData.map(entry => entry.deviceName)));

  const deviceNamesData = deviceNames.filter(deviceName =>
    auscData.some(entry => entry.deviceName === deviceName && entry.measurement !== null)
  );

  const deviceColorByName = useMemo(
    () => buildDeviceColorMap(deviceNamesData ?? []),
    [Array.isArray(deviceNamesData) ? deviceNamesData.join('\u0001') : '']
  );


  const AuscTooltip = ({ active, payload, label, vitals }) => {
    if (active && payload && payload.length) {
      const dataKey = payload[0].dataKey.split('.');
      const deviceName = dataKey[0];
      const data = payload[0].payload[deviceName] ?? payload[0].payload;
      
      // Find matching data from healthEvaluationData to get posture and activity level
      const matchingHealthData = healthEvaluationData.find(entry => 
        entry.deviceName === deviceName && 
        entry.timeStamp === data.timeStamp
      );
      
      return (
        <div className={styles.PerinTooltip}>
          <p style={{ fontSize:"16px"}}>{`${deviceName}: ${data["date"] ?? 'Date: N/A'}`}</p>
          {matchingHealthData?.posture && (
            <p style={{ fontSize:"16px"}}>{`Posture: ${matchingHealthData.posture}`}</p>
          )}
          {matchingHealthData?.activityLevel && (
            <p style={{ fontSize:"16px"}}>{`Activity Level: ${matchingHealthData.activityLevel}`}</p>
          )}
        </div>
      );
    }
  
    return null;
  };

  return (
    <div className={`${styles.cardContainer} ${className}`} ref={chartContainerRef}>
      <VitalCardHeader
        Icon={AuscultationIcon}
        vitalName={vitals.modalityName}
        measurement={displayData.measurement}
        latestDate={displayData.latestDate}
      />
      <div className={styles.chartWrapper} ref={ref}>
        <ResponsiveContainer width={"100%"} height={150}>
        <LineChart type="monotone" data={data} margin={{ bottom: 5 }}>
          <XAxis dataKey="date" fontSize={12} />
          <YAxis
            domain={0}
            tick={false}
          />
          <Tooltip content={<AuscTooltip vitals={vitals} />} />
          <Legend
            verticalAlign="top"
            align="right"
            height={30}
            iconType="square"
            wrapperStyle={{ fontSize: '14px', fontWeight: 'bold', width: '100%' }}
            payload={deviceNamesData.map((deviceName) => ({
              id: String(deviceName),
              value: deviceName,
              type: 'square',
              color: deviceColorByName[deviceName] || '#000000',
            }))}
            content={(props) => {
              const items = props.payload;
              if (!items?.length) return null;
              const isSingleDevice = items.length <= 1;
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: '12px',
                    paddingRight: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {items.map((entry) => {
                    const name = entry.value;
                    const hidden = !isSingleDevice && opacity[name] === 0;
                    const color = entry.color;
                    const swatch = (
                      <span
                        aria-hidden
                        style={{
                          width: '12px',
                          height: '12px',
                          flexShrink: 0,
                          backgroundColor: color,
                          borderRadius: '2px',
                        }}
                      />
                    );
                    if (isSingleDevice) {
                      return (
                        <span
                          key={String(name)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '2px 0',
                          }}
                        >
                          {swatch}
                          {name}
                        </span>
                      );
                    }
                    return (
                      <button
                        key={String(name)}
                        type="button"
                        aria-pressed={!hidden}
                        title={hidden ? `Show measurements from ${name}` : `Hide measurements from ${name}`}
                        onClick={() => handleLegendClick(entry)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '2px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                          color: 'inherit',
                          fontFamily: 'inherit',
                          opacity: hidden ? 0.45 : 1,
                          textDecoration: hidden ? 'line-through' : 'none',
                        }}
                      >
                        {swatch}
                        {name}
                      </button>
                    );
                  })}
                </div>
              );
            }}
          />
          {deviceNamesData.map((deviceName, index) => (
            <Line 
              connectNulls
              key={deviceName}
              type="linear"
              dataKey={deviceName + '.measurement'}
              stroke='#828282'
              strokeOpacity={opacity[deviceName] || 0}
              strokeWidth={3}
              dot={(props) => {
                const { cx, cy, value, key } = props;
                if (value !== undefined && value !== null) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill='white'
                      stroke={'#828282'}
                      strokeOpacity={opacity[deviceName] || 0}
                      strokeWidth={2}
                      key={key}
                      style={{ pointerEvents: 'visible' }}
                    />
                  );
                } else {
                  return null;
                }
              }}
              activeDot={(props) => {
                const { cx, cy, value, key, payload } = props;
                const dotColor = '#828282';
                if (value !== undefined && value !== null) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      onClick={(e) => {
                        e.stopPropagation(); // Ensure the event doesn't bubble up
                        handlePointClick(deviceName, payload[deviceName], {...vitals, modalityType: 'ausc'});
                      }}
                      style={{ pointerEvents: 'visible' }}
                      fill={dotColor === '#828282' ? `rgba(130, 130, 130, ${opacity[deviceName]})` : 'none'}
                      stroke={dotColor}
                      strokeOpacity={opacity[deviceName] || 0}
                      strokeWidth={2}
                      key={key}
                    />
                  );
                } else {
                  return null;
                }
              }}
            />
          ))}
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default AuscultationCardView;
